import type {
  AccountCampaignRowType,
  AccountCampaignStatus,
} from '../types/accountCampaign.js';

// Derived row-level status rollup (spec §6.2, amended by P2b-2 §6.2). Not
// stored on the row -- computed at render time from the ready gates,
// execution fields, rowType, and the location Account's status. The
// exhaustive set of display values is the union in
// src/types/accountCampaign.ts (AccountCampaignStatus).
//
// Precedence (highest → lowest):
//   0. `Opted Out` — rowType === 'opt_out'. Absolute override: the AM made
//      a deliberate decision that this location isn't participating. Beats
//      Cancelled Location too -- the opt-out signal is about intent, and
//      intent is what the grid should surface.
//   1. `Cancelled Location` — location Account is non-active (status !== 'active').
//      The rollup doesn't care what's been built on a row whose location
//      has left the brand hierarchy.
//   2. `Fully Complete` — both platforms in 'complete' state. "Complete" for
//      a platform means: ready, built, live, post-rollout-check, AND local-
//      built (if the row has a localAssetsUrl; otherwise not required).
//   3. Single-platform-only paths (one platform not ready):
//        - `Drafting` when neither ready
//        - `Meta Ready` / `Meta Built` / `Meta Live` when only Meta is ready
//        - same for Google
//        - `Meta Live` is the ceiling on a Meta-only row (there's no
//          `Meta Complete` display value — `Fully Complete` requires both).
//   4. Both ready: `Both Ready` when neither has built. Otherwise label the
//      lagging platform's state — that's the work that's still outstanding,
//      which is what the AM wants to see on a master row.
//
// UI colouring (spec §7.1): grey (Drafting), yellow (Ready/Both Ready),
// blue (Built), green (Live/Fully Complete), red (Cancelled Location).
// statusColorClass() returns a Tailwind token key so the master-grid pill
// stays in sync with whatever label comes out of this function.

type PlatformState = 'not_ready' | 'ready' | 'built' | 'live' | 'complete';

export type RowForStatus = {
  rowType: AccountCampaignRowType;
  metaReadyToBuild: boolean;
  googleReadyToBuild: boolean;
  metaBuilt: boolean;
  metaTurnedOn: boolean;
  metaLocalBuilt: boolean;
  metaPostRolloutCheck: boolean;
  googleBuilt: boolean;
  googleScheduled: boolean;
  googlePostRolloutCheck: boolean;
  localAssetsUrl: string | null;
};

function metaState(r: RowForStatus): PlatformState {
  if (!r.metaReadyToBuild) return 'not_ready';
  if (!r.metaBuilt) return 'ready';
  if (!r.metaTurnedOn) return 'built';
  const localOk = r.localAssetsUrl === null || r.metaLocalBuilt;
  if (!r.metaPostRolloutCheck || !localOk) return 'live';
  return 'complete';
}

function googleState(r: RowForStatus): PlatformState {
  if (!r.googleReadyToBuild) return 'not_ready';
  if (!r.googleBuilt) return 'ready';
  if (!r.googleScheduled) return 'built';
  if (!r.googlePostRolloutCheck) return 'live';
  return 'complete';
}

const RANK: Record<PlatformState, number> = {
  not_ready: 0,
  ready: 1,
  built: 2,
  live: 3,
  complete: 4,
};

function singlePlatformLabel(platform: 'Meta' | 'Google', s: PlatformState): AccountCampaignStatus {
  switch (s) {
    case 'not_ready':
      return 'Drafting'; // guarded by caller, but safe fallback
    case 'ready':
      return `${platform} Ready` as AccountCampaignStatus;
    case 'built':
      return `${platform} Built` as AccountCampaignStatus;
    case 'live':
    case 'complete':
      // No "Meta Complete" / "Google Complete" label — single-platform ceiling is Live.
      return `${platform} Live` as AccountCampaignStatus;
  }
}

export function computeAccountCampaignStatus(
  row: RowForStatus,
  accountStatus: 'active' | 'paused' | 'cancelled',
): AccountCampaignStatus {
  if (row.rowType === 'opt_out') return 'Opted Out';
  if (accountStatus !== 'active') return 'Cancelled Location';

  const m = metaState(row);
  const g = googleState(row);

  if (m === 'complete' && g === 'complete') return 'Fully Complete';

  if (m === 'not_ready' && g === 'not_ready') return 'Drafting';
  if (g === 'not_ready') return singlePlatformLabel('Meta', m);
  if (m === 'not_ready') return singlePlatformLabel('Google', g);

  if (m === 'ready' && g === 'ready') return 'Both Ready';

  // Both ready, at least one has moved past 'ready'. Label by the LAGGING
  // platform so the AM sees the work still outstanding. Ties go to Meta
  // (arbitrary but deterministic so the display doesn't flicker).
  const lagging: { platform: 'Meta' | 'Google'; state: PlatformState } =
    RANK[m] <= RANK[g] ? { platform: 'Meta', state: m } : { platform: 'Google', state: g };
  return singlePlatformLabel(lagging.platform, lagging.state);
}

// Pill colour token for the master-grid status cell. Tailwind class keys are
// resolved at the render site -- keeping the mapping here means a new status
// value added to the union forces a compile error at both locations.
export function statusPillClass(status: AccountCampaignStatus): string {
  switch (status) {
    case 'Opted Out':
      return 'bg-slate-700 text-white';
    case 'Drafting':
      return 'bg-slate-100 text-slate-700';
    case 'Meta Ready':
    case 'Google Ready':
    case 'Both Ready':
      return 'bg-yellow-100 text-yellow-800';
    case 'Meta Built':
    case 'Google Built':
      return 'bg-blue-100 text-blue-800';
    case 'Meta Live':
    case 'Google Live':
    case 'Fully Complete':
      return 'bg-emerald-100 text-emerald-800';
    case 'Cancelled Location':
      return 'bg-red-100 text-red-800';
  }
}
